/**
 * Global job-progress store.
 *
 * Tracks every in-flight admin job (shop-reset, mockup-regen, listing-replace,
 * bulk upload) as a normalized descriptor so a single provider can render an
 * auto-opening modal + minimizable top-right toasts. Concurrent jobs are held in
 * a map; `focusedJobId` is the one shown in the expanded modal, the rest render
 * as toasts.
 *
 * Persisted (localStorage `cp-job-progress`) so an in-flight job survives a
 * reload — but only ACTIVE jobs are persisted, MINIMIZED, with no focus, so a
 * reload never pops a modal for a job that already finished. Adapters (functions)
 * are NOT persisted; they're re-attached by `kind` at render via getAdapter().
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { isTerminal } from '../lib/jobStatus';

const useJobProgressStore = create(
  persist(
    (set, get) => ({
      // jobs: { [id]: { id, kind, label, accent, meta, raw, _minimized, _notified } }
      jobs: {},
      focusedJobId: null,

      // Register a freshly-started job and auto-open its modal.
      trackJob: ({ id, kind, label, accent, meta }) => {
        if (!id) return;
        set(state => ({
          focusedJobId: id,
          jobs: {
            ...state.jobs,
            [id]: {
              id,
              kind,
              label,
              accent,
              meta: meta || {},
              raw: state.jobs[id]?.raw || { status: 'queued' },
              _minimized: false,
              _notified: state.jobs[id]?._notified || false,
              _trackedAt: state.jobs[id]?._trackedAt || Date.now(),
            },
          },
        }));
      },

      // Drop persisted jobs older than maxAgeMs (default 24h). A job that
      // never reached a terminal status (server purged it, old client bug, …)
      // must not survive in localStorage forever — a stale entry kept the
      // global poller hammering its status endpoint across sessions
      // (2026-07-04 prod 401 flood).
      evictStale: (maxAgeMs = 24 * 60 * 60 * 1000) =>
        set(state => {
          const cutoff = Date.now() - maxAgeMs;
          const kept = Object.entries(state.jobs).filter(
            ([, v]) => (v._trackedAt || 0) > cutoff && !isTerminal(v.raw?.status)
          );
          if (kept.length === Object.keys(state.jobs).length) return state;
          const jobs = Object.fromEntries(kept);
          return {
            jobs,
            focusedJobId: state.focusedJobId && jobs[state.focusedJobId] ? state.focusedJobId : null,
          };
        }),

      // Merge a fresh raw status payload (from the poller or the upload watcher).
      // Creates the entry if missing (so the watcher can adopt server-discovered
      // jobs), but does NOT steal focus or un-minimize.
      updateJob: (id, raw, descriptor) =>
        set(state => {
          const job = state.jobs[id];
          if (!job) {
            if (!descriptor) return state;
            return {
              jobs: {
                ...state.jobs,
                [id]: {
                  id,
                  kind: descriptor.kind,
                  label: descriptor.label,
                  accent: descriptor.accent,
                  meta: descriptor.meta || {},
                  raw,
                  _minimized: true, // watcher-discovered jobs start as a toast
                  _notified: false,
                  _trackedAt: Date.now(),
                },
              },
            };
          }
          return {
            jobs: {
              ...state.jobs,
              [id]: { ...job, raw, meta: descriptor?.meta ? { ...job.meta, ...descriptor.meta } : job.meta },
            },
          };
        }),

      minimize: id =>
        set(state => ({
          focusedJobId: state.focusedJobId === id ? null : state.focusedJobId,
          jobs: state.jobs[id] ? { ...state.jobs, [id]: { ...state.jobs[id], _minimized: true } } : state.jobs,
        })),

      expand: id =>
        set(state =>
          state.jobs[id]
            ? { focusedJobId: id, jobs: { ...state.jobs, [id]: { ...state.jobs[id], _minimized: false } } }
            : state
        ),

      markNotified: id =>
        set(state =>
          state.jobs[id] ? { jobs: { ...state.jobs, [id]: { ...state.jobs[id], _notified: true } } } : state
        ),

      dismiss: id => get().removeJob(id),

      removeJob: id =>
        set(state => {
          if (!state.jobs[id]) return state;
          const { [id]: _drop, ...rest } = state.jobs;
          return { jobs: rest, focusedJobId: state.focusedJobId === id ? null : state.focusedJobId };
        }),

      clearAll: () => set({ jobs: {}, focusedJobId: null }),

      // --- selectors (call as plain functions) ---
      allJobs: () => Object.values(get().jobs),
      activeJobs: () => Object.values(get().jobs).filter(j => !isTerminal(j.raw?.status)),
      focusedJob: () => {
        const { jobs, focusedJobId } = get();
        return focusedJobId ? jobs[focusedJobId] || null : null;
      },
    }),
    {
      name: 'cp-job-progress',
      // Persist only active jobs, minimized, with no focus — a reload should
      // re-surface in-flight work as a toast, never auto-pop a stale modal.
      partialize: state => ({
        jobs: Object.fromEntries(
          Object.entries(state.jobs)
            .filter(([, v]) => !isTerminal(v.raw?.status))
            .map(([k, v]) => [k, { ...v, _minimized: true }])
        ),
      }),
    }
  )
);

export default useJobProgressStore;
